import { useCreateFieldGetter } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';

/**
 * Base composable for article button customisation
 * Returns only common button fields that all buttons share
 * Can be extended by specific button composables for additional fields
 *
 * @param sectionKey - The section key for the specific button
 * @returns Object containing common button fields and getField function for extensions
 */
const useArticleButtonCustomisationBase = (sectionKey: string) => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE,
    sectionKey,
    blockKey: PUI.ARTICLES_BLOCK_KEYS.BODY_SECTION,
  });

  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);

  return {
    isVisible,
    buttonTextColor,
    buttonBackgroundColor,
    buttonBorderColor,
    getField, // Expose getField for extensions
  };
};

/**
 * Composable for Article Back Button customisation
 * Returns back button customization with buttonText field
 * Can be extended with back-button-specific fields if needed in the future
 *
 * @example
 * // To add back-button-specific fields in the future:
 * export const useArticleBackButtonCustomisation = () => {
 *   const base = useArticleButtonCustomisationBase(PUI.ARTICLES_SECTION_KEYS.ARTICLE_BACK_BUTTON);
 *   const { getField, ...rest } = base;
 *   const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
 *   const backButtonIcon = getField('backButtonIcon');
 *   return { ...rest, buttonText, backButtonIcon };
 * };
 */
export const useArticleBackButtonCustomisation = () => {
  const base = useArticleButtonCustomisationBase(PUI.ARTICLES_SECTION_KEYS.ARTICLE_BACK_BUTTON);
  const { getField, ...rest } = base;
  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  return { ...rest, buttonText };
};

/**
 * Composable for Article Contact Options Button customisation
 * Returns contact options button customization with buttonText field
 * Can be extended with contact-button-specific fields if needed in the future
 *
 * @example
 * // To add contact-button-specific fields in the future:
 * export const useArticleContactOptionsButtonCustomisation = () => {
 *   const base = useArticleButtonCustomisationBase(PUI.ARTICLES_SECTION_KEYS.ARTICLE_CONTACT_OPTIONS_BUTTON);
 *   const { getField, ...rest } = base;
 *   const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
 *   const contactIcon = getField('contactIcon');
 *   return { ...rest, buttonText, contactIcon };
 * };
 */
export const useArticleContactOptionsButtonCustomisation = () => {
  const base = useArticleButtonCustomisationBase(PUI.ARTICLES_SECTION_KEYS.ARTICLE_CONTACT_OPTIONS_BUTTON);
  const { getField, ...rest } = base;
  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  return { ...rest, buttonText };
};

/**
 * Composable for Article Action To Be Taken Button customisation
 * Returns action button customization
 * Note: This button doesn't include buttonText as the text comes from article config
 * Can be extended with action-button-specific fields if needed in the future
 *
 * @example
 * // To add action-button-specific fields in the future:
 * export const useArticleActionToBeTakenButtonCustomisation = () => {
 *   const base = useArticleButtonCustomisationBase(ARTICLES_SECTION_KEYS.ARTICLE_ACTION_TO_BE_TAKEN_BUTTON);
 *   const { getField, ...rest } = base;
 *   const actionIcon = getField('actionIcon');
 *   return { ...rest, actionIcon };
 * };
 */
export const useArticleActionToBeTakenButtonCustomisation = () => {
  const base = useArticleButtonCustomisationBase(PUI.ARTICLES_SECTION_KEYS.ARTICLE_ACTION_TO_BE_TAKEN_BUTTON);
  const { getField, ...rest } = base;
  return rest;
};
